import { Controller } from "@hotwired/stimulus"

// Drives one field-mapping row in the mappings editor. The function <select>
// chooses which per-function panel is visible (each panel carries
// data-function="<key>"); switching it shows the matching panel and submits
// the row so the canonical args for the new function are persisted and the
// preview re-runs. Also owns the concat panel's "add part" / "remove part"
// buttons, cloning a hidden template row and renumbering the array indices so
// Rails receives a contiguous parts[] array.
export default class extends Controller {
  static targets = [
    "form", "panel",
    "partsList", "partTemplate",
    "stepsList", "stepTemplate",
    "branchesList", "branchTemplate",
  ]

  connect() {
    this.togglePanels()
  }

  // Show only the panel matching the current function and disable the hidden
  // panels' fields so they don't submit (otherwise a hidden [args][column]
  // would clobber the active panel's value). The autosave controller on the
  // same <form> handles the actual save on change.
  togglePanels() {
    const fn = this.functionValue
    this.panelTargets.forEach((el) => {
      const active = el.dataset.function === fn
      el.hidden = !active
      el.querySelectorAll("input, select, textarea").forEach((field) => {
        field.disabled = !active
      })
    })
  }

  get functionValue() {
    const select = this.element.querySelector("[data-mapping-row-function]")
    return select ? select.value : null
  }

  addPart(event) {
    event.preventDefault()
    const index = this.partsListTarget.children.length
    const html = this.partTemplateTarget.innerHTML.replaceAll("NEW_INDEX", index)
    this.partsListTarget.insertAdjacentHTML("beforeend", html)
  }

  removePart(event) {
    event.preventDefault()
    event.target.closest("[data-mapping-part]").remove()
    this.reindexParts()
    this.element.requestSubmit()
  }

  // After a removal the surviving parts must reindex to 0..n so Rails parses a
  // dense parts[] array instead of a sparse hash.
  reindexParts() {
    this.reindex(this.partsListTarget, "[data-mapping-part]", "parts")
  }

  addStep(event) {
    event.preventDefault()
    const index = this.stepsListTarget.children.length
    const html = this.stepTemplateTarget.innerHTML.replaceAll("NEW_INDEX", index)
    this.stepsListTarget.insertAdjacentHTML("beforeend", html)
  }

  removeStep(event) {
    event.preventDefault()
    event.target.closest("[data-mapping-step]").remove()
    this.reindex(this.stepsListTarget, "[data-mapping-step]", "steps")
    this.element.requestSubmit()
  }

  addBranch(event) {
    event.preventDefault()
    const index = this.branchesListTarget.children.length
    const html = this.branchTemplateTarget.innerHTML.replaceAll("NEW_INDEX", index)
    this.branchesListTarget.insertAdjacentHTML("beforeend", html)
  }

  removeBranch(event) {
    event.preventDefault()
    event.target.closest("[data-mapping-branch]").remove()
    this.reindex(this.branchesListTarget, "[data-mapping-branch]", "branches")
    this.element.requestSubmit()
  }

  // Renumber the field names of a list's rows to a dense 0..n so Rails parses a
  // contiguous array instead of a sparse hash after a removal.
  reindex(list, rowSelector, key) {
    const pattern = new RegExp(`\\[${key}\\]\\[\\d+\\]`)
    list.querySelectorAll(rowSelector).forEach((row, i) => {
      row.querySelectorAll("[name]").forEach((field) => {
        field.name = field.name.replace(pattern, `[${key}][${i}]`)
      })
    })
  }
}
